import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { plainToInstance } from 'class-transformer';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { API } from '~/api';
import { UIService } from '~/service/service.ui';
import { PromotionBestsellerDto, PromotionNewbieDto, PromotionOfferDto } from '~/shared/dto/admin/promotion.dto';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { FieldSelect } from '../fields/field.select';
import { Col, Row, Text } from '../view.box';

export const PromotionOfferDialog = observer<{
	fragrances: API.FragrancePromo[];
	initial?: PromotionOfferDto;
	onApply?: (offer: PromotionOfferDto) => void;
}>(({ fragrances, initial, onApply }) => {
	const [ui] = useService(UIService);

	const form = Form.use(PromotionOfferDto, {
		name: 'PromotionOfferDto',
		initial: plainToInstance(PromotionOfferDto, initial),
		onSubmit: dto => {
			if (onApply) onApply(dto);
			ui.hideDialog();
		},
	});

	useEffect(() => {
		if (initial) form.apply(initial);
	}, [form, initial]);

	const offers = useMemo(
		() => fragrances.find(f => f.id === +form.$fragranceId.value)?.offers ?? [],
		[fragrances, form.$fragranceId.value],
	);

	return (
		<Dialog open={ui.dialog === PromotionOfferDialog} maxWidth="sm" fullWidth>
			<DialogTitle>Offer</DialogTitle>
			<DialogContent>
				<Col gap={2} mt={1}>
					<FieldSelect
						field={form.$fragranceId}
						items={fragrances}
						valueProp="id"
						labelProp="name"
						enabledProp="active"
						nullLabel="No options"
						disabled={!!initial?.fragranceId}
					/>
					<FieldSelect
						field={form.$storeOfferId}
						items={offers}
						valueProp="id"
						enabledProp="available"
						labelProp={item => (
							<Row flex={1} gap={1} justifyContent="space-between">
								<Text flex={1}>{item.storeName}</Text>
								<Row gap={1} justifyContent="flex-start">
									{item.discountPrice && (
										<Text color="warning.main">
											-{(((item.price - item.discountPrice) * 100) / item.price).toFixed(0)}%
										</Text>
									)}
									<Text fontWeight="bold">
										{item.discountPrice || item.price} {item.currency}
									</Text>
								</Row>
							</Row>
						)}
					/>
				</Col>
			</DialogContent>
			<PromotionDialogActions onApply={form.submit} />
		</Dialog>
	);
});

export const PromotionBestsellerDialog = observer<{
	fragrances: API.FragrancePromo[];
	initial?: PromotionBestsellerDto;
	onApply?: (offer: PromotionBestsellerDto) => void;
}>(({ fragrances, initial, onApply }) => {
	const [ui] = useService(UIService);

	const form = Form.use(PromotionBestsellerDto, {
		name: 'PromotionBestsellerDto',
		initial: plainToInstance(PromotionBestsellerDto, initial),
		onSubmit(dto) {
			if (onApply) onApply(dto);
			ui.hideDialog();
		},
	});

	useEffect(() => {
		if (initial) form.apply(initial);
	}, [form, initial]);

	return (
		<Dialog open={ui.dialog === PromotionBestsellerDialog} maxWidth="sm" fullWidth>
			<DialogTitle>Bestseller</DialogTitle>
			<DialogContent>
				<Col gap={2} mt={1}>
					<FieldSelect
						field={form.$fragranceId}
						items={fragrances}
						valueProp="id"
						labelProp="name"
						enabledProp="active"
						nullLabel="No options"
						disabled={!!initial?.fragranceId}
					/>
				</Col>
			</DialogContent>
			<PromotionDialogActions onApply={form.submit} />
		</Dialog>
	);
});

export const PromotionNewbieDialog = observer<{
	fragrances: API.FragrancePromo[];
	initial?: PromotionNewbieDto;
	onApply?: (offer: PromotionNewbieDto) => void;
}>(({ fragrances, initial, onApply }) => {
	const [ui] = useService(UIService);

	const form = Form.use(PromotionNewbieDto, {
		name: 'PromotionNewbieDto',
		initial: plainToInstance(PromotionNewbieDto, initial),
		onSubmit(dto) {
			if (onApply) onApply(dto);
			ui.hideDialog();
		},
	});

	useEffect(() => {
		if (initial) form.apply(initial);
	}, [form, initial]);

	return (
		<Dialog open={ui.dialog === PromotionNewbieDialog} maxWidth="sm" fullWidth>
			<DialogTitle>Newbie</DialogTitle>
			<DialogContent>
				<Col gap={2} mt={1}>
					<FieldSelect
						field={form.$fragranceId}
						items={fragrances}
						valueProp="id"
						labelProp="name"
						enabledProp="active"
						nullLabel="No options"
						disabled={!!initial?.fragranceId}
					/>
				</Col>
			</DialogContent>
			<PromotionDialogActions onApply={form.submit} />
		</Dialog>
	);
});

const PromotionDialogActions = observer<{ onApply: () => void }>(({ onApply }) => {
	const [ui] = useService(UIService);

	return (
		<DialogActions>
			<Button onClick={ui.hideDialog}>Close</Button>
			<Button variant="contained" onClick={onApply}>
				Apply
			</Button>
		</DialogActions>
	);
});
